/**
 * Os passos de apresentação de um frame — a parte que DECIDE, sem tocar em nada.
 *
 * ─── por que este arquivo existe separado ───────────────────────────────────────────────────
 *
 * Duas razões, e a segunda é a que decide (é o mesmo argumento do `surface/src/tuninho/
 * decidir-pan.ts`, e ele se pagou lá):
 *
 * 1. `frame-model.ts` e `edgeless-clipboard-config.ts` são arquivos do UPSTREAM. Tudo que se
 *    escreve neles vira patch a manter a cada atualização do Affine (Regra 8.15 — *arquivo
 *    novo não conflita; arquivo editado conflita*). O que sobra lá é a fiação; a decisão mora
 *    aqui.
 *
 * 2. **Este arquivo não importa NADA.** Sem `@blocksuite`, sem tipo do DOM, sem zod. É isso
 *    que permite `cli/tests/js/passos-de-apresentacao.test.ts` executá-lo de verdade nesta
 *    estação, sem `node_modules` do Affine e sem os ~16 minutos de build que a Regra 8.7.3
 *    cataloga.
 *
 *    ⚠️ No dia em que precisar importar de `@blocksuite`, ele deixou de ser puro e o teste
 *    para de carregar. Isso é o gate, não um acidente.
 *
 * ─── o modelo, em uma frase ─────────────────────────────────────────────────────────────────
 *
 * `presentationSteps` é `Record<elementId, índiceFracionário>` no frame. Marcar = escrever a
 * chave. Desmarcar = apagar. Ordenar = gerar um índice novo entre os vizinhos — **exatamente**
 * o que `presentationIndex` já faz um nível acima, para a ordem dos frames.
 */

/** `elementId` → índice fracionário. A mesma forma que `presentationIndex` usa para frames. */
export type Passos = Record<string, string>;

/**
 * Um passo já resolvido: o elemento, o índice que o ordena e o número que o usuário vê.
 *
 * `numero` começa em **1** porque é o que vai no badge do canvas. Empate produz números
 * iguais — ver `ordenarPassos`.
 */
export interface PassoResolvido {
  id: string;
  indice: string;
  numero: number;
}

/**
 * Aceita o valor cru vindo de um snapshot (`BlockSnapshot['props']` é `Record<string, unknown>`)
 * e devolve `Passos` ou `undefined`.
 *
 * ⚠️ Chave cujo valor não é string é DESCARTADA, não convertida. Um `presentationSteps` com
 * `{ id: true }` seria a Opção D disfarçada — o slot de valor que a §8.5 do dossiê mostra que
 * o clipboard do upstream já preenche com `true` fixo. Se isso chegar aqui, é lixo de outra
 * escrita, não ordem.
 */
export function lerPassos(cru: unknown): Passos | undefined {
  if (typeof cru !== 'object' || cru === null || Array.isArray(cru)) return undefined;

  const limpos: Passos = {};
  let algum = false;
  for (const [id, valor] of Object.entries(cru as Record<string, unknown>)) {
    if (typeof valor === 'string' && valor.length > 0) {
      limpos[id] = valor;
      algum = true;
    }
  }
  return algum ? limpos : undefined;
}

/**
 * A lista ordenada de passos de um frame — **filtrada pelos filhos que o frame ainda tem**.
 *
 * ⚠️ O filtro por `idsDoFrame` é a linha que a §8.5 do dossiê diz que se esquece, e o custo de
 * esquecê-la não é um erro: é uma lista com item fantasma e uma contagem que mente. Os dois
 * cenários que a produzem já existem no upstream e nenhum deles falha:
 *
 *   - `removeChildren()` (`frame-model.ts`) apaga a chave de `childElementIds` e não sabe que
 *     `presentationSteps` existe;
 *   - `removeAllChildrenFromFrame()` (`frame-manager.ts:480`) zera o mapa inteiro.
 *
 * Filtrar na LEITURA é o que torna o Patch 3 do card desnecessário: a chave órfã pode existir
 * no documento e não tem como ser vista. Ela só some de verdade quando alguém marca ou
 * desmarca algo naquele frame — ver `podarOrfaos`.
 *
 * A ordenação é lexicográfica no índice fracionário (é para isso que `generateKeyBetweenV2`
 * existe), com o `id` como desempate estável. **Empate é permitido de propósito** (§9.5 do
 * dossiê): dois elementos com o mesmo índice revelam juntos, e permitir isso agora custa
 * nada — retroagir depois seria migração de dado num CRDT.
 */
export function ordenarPassos(
  passos: Passos | undefined,
  idsDoFrame: readonly string[]
): PassoResolvido[] {
  if (!passos) return [];

  const doFrame = new Set(idsDoFrame);
  const vivos = Object.entries(passos).filter(([id]) => doFrame.has(id));

  vivos.sort(([idA, a], [idB, b]) => (a === b ? (idA < idB ? -1 : 1) : a < b ? -1 : 1));

  // Empate compartilha o número: quem tem o mesmo índice é o mesmo passo.
  const resolvidos: PassoResolvido[] = [];
  let numero = 0;
  let indiceAnterior: string | null = null;
  for (const [id, indice] of vivos) {
    if (indice !== indiceAnterior) {
      numero += 1;
      indiceAnterior = indice;
    }
    resolvidos.push({ id, indice, numero });
  }
  return resolvidos;
}

/**
 * Os elementos que ainda vão ser revelados em frames que a apresentação **não alcançou**.
 *
 * Card 1864714406189860111. O operador viu o `Start` — um frame panorâmico, sem passos — e
 * relatou que ele *"exibe os elementos dos frames que ainda vão ser exibidos por passos"*.
 * Ele estava certo sobre o efeito, e a causa não é defeito de implementação: até aqui o
 * controlador olhava **só o frame atual**, então um elemento marcado no `Frame 4` aparecia
 * normalmente enquanto se estava no `Start`, e sumia ao entrar no `Frame 4` para voltar passo
 * a passo. Quem assiste vê a mesma caixa duas vezes, e a segunda como "novidade".
 *
 * A regra que o operador enunciou é mais simples que a implementada: **marcar um elemento
 * como passo é dizer que ele não existe até a sua vez.** Esta função é a metade que faltava —
 * o conjunto do que fica escondido por pertencer ao FUTURO da apresentação.
 *
 * ⚠️ Recebe os passos já resolvidos de cada frame futuro, e não os frames: é o que mantém
 * este arquivo sem importar nada (ver o cabeçalho) e, portanto, executável em teste sem o
 * `node_modules` do Affine.
 */
export function ocultosDosFuturos(
  passosPorFrameFuturo: readonly (readonly PassoResolvido[])[]
): string[] {
  const ids = new Set<string>();
  for (const passos of passosPorFrameFuturo) {
    for (const p of passos) ids.add(p.id);
  }
  return [...ids];
}

/** Quantos passos distintos o frame tem (empate conta uma vez). */
export function totalDePassos(ordenados: readonly PassoResolvido[]): number {
  return ordenados.length === 0 ? 0 : ordenados[ordenados.length - 1].numero;
}

/**
 * Os elementos que o passo `numero` revela. Plural porque empate existe (§9.5).
 */
export function elementosDoPasso(
  ordenados: readonly PassoResolvido[],
  numero: number
): string[] {
  return ordenados.filter(p => p.numero === numero).map(p => p.id);
}

/**
 * Os elementos que devem estar OCULTOS quando `revelados` passos já foram mostrados.
 *
 * `revelados = 0` é o estado em que o frame entra na apresentação: tudo que tem passo está
 * escondido. `revelados >= total` é o estado final: nada escondido.
 */
export function ocultosApos(
  ordenados: readonly PassoResolvido[],
  revelados: number
): string[] {
  return ordenados.filter(p => p.numero > revelados).map(p => p.id);
}

/**
 * O avanço tem um passo pendente para revelar?
 *
 * ⚠️ **Esta função substituiu um ouvinte de teclado, e a troca é a lição do 1º ciclo de
 * validação em campo.**
 *
 * A primeira versão enganchava `keydown` em fase de captura para ganhar da barra de
 * apresentação — a "Armadilha #1" do card, que é real: `dispatcher.add()` faz `unshift`
 * (`event/dispatcher.ts:361`) e a `PresentationToolbar` registra por último, logo roda
 * primeiro. A captura ganhava a corrida.
 *
 * O que ela **não** ganhava era o BOTÃO. A seta da barra é um clique que chama `_nextFrame()`
 * direto e não passa por `keydown` nenhum — então a tecla revelava os elementos e o botão
 * pulava para o próximo quadro **deixando-os escondidos**, sem ninguém para devolvê-los. O
 * operador viu em campo, e a frase dele nomeia o defeito melhor que qualquer teste:
 * *"ambas precisam seguir a exibição dos elementos"*.
 *
 * Um mesmo comando com dois comportamentos não é falha de acabamento — é o sinal de que o
 * gancho está no lugar errado. O gancho certo é o **avanço** (`_nextFrame`/`_previousFrame`),
 * por onde passam os dois caminhos: aí eles ficam iguais **por construção**, e a corrida deixa
 * de existir, porque quem chama passamos a ser nós.
 */
export function podeRevelar(estado: { total: number; revelados: number }): boolean {
  return estado.revelados < estado.total;
}

/**
 * O retrocesso tem um passo revelado para esconder de volta?
 *
 * ⚠️ **A primeira versão decidiu que `←` era só do quadro — e havia teste afirmando isso.**
 * Estava errado, e o operador foi direto ao ponto: se `→` revela um a um, `←` desfaz um a um.
 * É o comportamento do Prezi e do PowerPoint, e é o único que deixa uma apresentação
 * navegável — voltar para reforçar o passo anterior é gesto de quem apresenta.
 *
 * Só quando não há mais o que esconder é que `←` passa para o quadro anterior.
 */
export function podeDesrevelar(estado: { revelados: number }): boolean {
  return estado.revelados > 0;
}

/**
 * Os índices vizinhos de um destino numa reordenação — o par que vai para
 * `generateKeyBetweenV2(antes, depois)`.
 *
 * Não gera a chave aqui de propósito: `generateKeyBetweenV2` mora em `@blocksuite/std/gfx` e
 * importá-la tiraria a pureza deste arquivo. É o mesmo desenho que `frame-order-menu.ts:187-196`
 * já usa para frames — ler os dois vizinhos, gerar entre eles.
 *
 * `destino` é a posição na lista JÁ SEM o item que está sendo movido, como o
 * `frame-order-menu` calcula.
 */
export function vizinhosParaMover(
  ordenados: readonly PassoResolvido[],
  idMovido: string,
  destino: number
): { antes: string | null; depois: string | null } {
  const semOMovido = ordenados.filter(p => p.id !== idMovido);
  const limitado = Math.max(0, Math.min(destino, semOMovido.length));
  return {
    antes: limitado > 0 ? semOMovido[limitado - 1].indice : null,
    depois: limitado < semOMovido.length ? semOMovido[limitado].indice : null,
  };
}

/**
 * Remapeia os passos ao COPIAR E COLAR um frame — o Patch 2, e o motivo de ele existir.
 *
 * A §8.5 do dossiê mediu o mecanismo: `edgeless-clipboard-config.ts:17` desestrutura uma
 * **lista fechada** de props (`xywh`, `title`, `background`, `childElementIds`) e repassa só
 * essas cinco ao criar o bloco novo. Uma prop que não esteja nessa lista não dá erro — ela
 * simplesmente **não existe** no frame colado.
 *
 * O cenário que isso quebra é o mais provável de todos: quem monta uma apresentação duplica o
 * frame (⌘D) para fazer a variação seguinte. Sem este remapeamento, o frame novo nasce sem
 * marcação nenhuma — sem aviso, sem log, sem nada. **Quem testa com um frame nunca vê.**
 *
 * ⚠️ Elemento cujo id não foi remapeado é DESCARTADO, não mantido com o id velho. Manter
 * apontaria para um elemento do frame de origem, e o filtro de `ordenarPassos` o esconderia —
 * lixo silencioso em vez de erro.
 */
export function remapearPassos(
  cru: unknown,
  mapaDeIds: ReadonlyMap<string, string>
): Passos | undefined {
  const passos = lerPassos(cru);
  if (!passos) return undefined;

  const novos: Passos = {};
  let algum = false;
  for (const [idAntigo, indice] of Object.entries(passos)) {
    const idNovo = mapaDeIds.get(idAntigo);
    if (idNovo) {
      novos[idNovo] = indice;
      algum = true;
    }
  }
  return algum ? novos : undefined;
}

/**
 * Tira do mapa as chaves de elementos que não são mais filhos do frame.
 *
 * A leitura já filtra (`ordenarPassos`), então isto não é correção de defeito — é higiene, e
 * roda **só quando alguém já está escrevendo** naquele frame (marcar/desmarcar/reordenar).
 * Rodar em toda leitura escreveria no documento de quem só está olhando, que é exatamente o
 * que a decisão 9.4 do dossiê proíbe para a apresentação.
 *
 * Devolve `null` quando não há nada a podar — o chamador usa isso para não escrever à toa e
 * não sujar o histórico de undo.
 */
export function podarOrfaos(
  passos: Passos | undefined,
  idsDoFrame: readonly string[]
): Passos | null {
  if (!passos) return null;
  const doFrame = new Set(idsDoFrame);
  const vivos = Object.entries(passos).filter(([id]) => doFrame.has(id));
  if (vivos.length === Object.keys(passos).length) return null;
  return Object.fromEntries(vivos);
}

/**
 * O mapa depois de marcar um elemento no fim da fila.
 *
 * Devolve o par `{ antes, depois }` para o chamador gerar a chave — mesma divisão de trabalho
 * de `vizinhosParaMover`, pela mesma razão (pureza).
 */
export function vizinhosParaMarcarNoFim(ordenados: readonly PassoResolvido[]): {
  antes: string | null;
  depois: string | null;
} {
  return {
    antes: ordenados.length > 0 ? ordenados[ordenados.length - 1].indice : null,
    depois: null,
  };
}

/** O mapa sem um elemento — desmarcar. Devolve `undefined` quando esvazia. */
export function desmarcar(passos: Passos | undefined, id: string): Passos | undefined {
  if (!passos || !(id in passos)) return passos;
  const { [id]: _fora, ...resto } = passos;
  return Object.keys(resto).length > 0 ? resto : undefined;
}

/** O mapa com um elemento marcado no índice dado. */
export function marcar(
  passos: Passos | undefined,
  id: string,
  indice: string
): Passos {
  return { ...(passos ?? {}), [id]: indice };
}

/** O elemento já tem passo neste frame? */
export function estaMarcado(passos: Passos | undefined, id: string): boolean {
  return !!passos && typeof passos[id] === 'string';
}
